import {
  Avatar,
  AvatarFallback,
  Button,
  Heading,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import { useEffect, useMemo, useState } from "react";

import { ConfirmationModalBase } from "@/components/common/modals";
import { useAuthorization } from "@/hooks/use-authorization";
import {
  useDeleteEvent,
  useGetCalendarEvents,
  useGetCurrentFiscalYear,
} from "@/queries/calendar/calendar.api";
import { Permissions } from "@/utils/permissions";
import { cn } from "@/utils/theme";
import CalendarControls from "./components/calendar/calendar-controls";
import CalendarTable from "./components/calendar/calendar-table";
import ConfigModal from "./components/modals/config-modal";
import CreateEvent from "./components/modals/create-event";
import DayModal from "./components/modals/day-modal";
import {
  getDaysInMonth,
  getEventsForDate,
  mapNumbersToDays,
  type DayType,
  type EventForDay,
  type EventType,
} from "./components/utils/utils";

export interface IDaySelected {
  date: Date;
  isAvailable: boolean;
  hasEventBlocked: boolean;
  events: EventForDay[];
}

const Calendar = () => {
  const { showToast } = useToast();
  const { hasPermission } = useAuthorization();
  const { data: fiscalYear } = useGetCurrentFiscalYear();

  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [openDayDrawer, setOpenDayDrawer] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<
    "config" | "event" | "confirmation" | "idle"
  >("idle");
  const [eventWeekend, setEventWeekend] = useState<EventType | undefined>(
    undefined,
  );
  const [enableDay, setEnableDay] = useState<DayType[]>([]);
  const [eventSelected, setEvenSelected] = useState<EventForDay | undefined>(
    undefined,
  );
  const [selectedDay, setSelectedDay] = useState<IDaySelected>({
    date: new Date(),
    isAvailable: false,
    hasEventBlocked: false,
    events: [],
  });

  const canCreate = hasPermission(Permissions["calendar"]["create"]);

  const days = getDaysInMonth(currentDate);
  const rows: Array<Date[]> = useMemo(() => {
    const r: Array<Date[]> = [];
    for (let i = 0; i < days.length; i += 7) {
      r.push(days.slice(i, i + 7));
    }
    return r;
  }, [days]);

  const { data: eventsData, isFetching: isLoading } = useGetCalendarEvents(
    rows,
    ["createdBy"],
  );

  useEffect(() => {
    if (openDayDrawer) {
      const events = getEventsForDate(eventsData ?? [], selectedDay.date);
      const hasEventBlocked =
        events.filter((e) =>
          ["HOLIDAY", "NON_WORKING_DAY", "WEEKEND"].includes(e.type),
        ).length > 0;

      setSelectedDay((prev) => ({
        ...prev,
        hasEventBlocked: hasEventBlocked,
        events: events,
      }));
    }
  }, [eventsData, openDayDrawer, selectedDay.date]);

  useEffect(() => {
    if (eventsData) {
      const weekend = eventsData?.find(
        (e) => e.type === "WEEKEND" && e.recurrence_meta,
      );

      setEventWeekend(weekend);
      const days: DayType[] = weekend
        ? mapNumbersToDays(weekend.recurrence_meta?.weekdays ?? [])
        : ["DOM", "SAB"];

      setEnableDay(days);
    }
  }, [eventsData]);

  const handlePrevious = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1),
    );
  };

  const handleNext = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1),
    );
  };

  const backToDay = () => {
    setCurrentDate(new Date());
  };

  const onClose = () => {
    setOpenModal("idle");
    setEvenSelected(undefined);
    if (!openDayDrawer) {
      setSelectedDay({
        date: new Date(),
        isAvailable: false,
        hasEventBlocked: false,
        events: [],
      });
    }
  };

  const handleSubmit = (selectedDays: DayType[]) => {
    setEnableDay(selectedDays);
  };

  const handleEditEvent = (event: EventForDay) => {
    setEvenSelected(event);
    setOpenModal("event");
  };

  const { mutateAsync: deleteEvent } = useDeleteEvent();

  const handleDeleteEvent = async () => {
    try {
      deleteEvent(eventSelected!.id as string);
      showToast({
        title: "Evento de calendario",
        description: "El evento fue eliminado correctamente",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square" type="icon">
                <Icon type="fas" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch (e) {
      const error = e as Error;
      showToast({
        title: "Error al editar registro",
        description: error.message,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    } finally {
      onClose();
    }
  };

  return (
    <>
      {openModal === "config" && (
        <ConfigModal
          isOpen={openModal === "config"}
          onClose={onClose}
          handleSubmit={handleSubmit}
          enableDay={enableDay}
          currentFiscalYear={fiscalYear}
          eventWeekend={eventWeekend}
        />
      )}

      {openModal === "event" && (
        <CreateEvent
          isOpen={openModal === "event"}
          onClose={onClose}
          selectedDay={selectedDay}
          eventSelected={eventSelected}
          handleSubmit={() => setOpenModal("confirmation")}
        />
      )}

      {openDayDrawer && (
        <DayModal
          isOpen={openDayDrawer}
          onClose={() => setOpenDayDrawer(false)}
          selectedDay={selectedDay}
          handleSubmit={() => setOpenModal("event")}
          handleEditEvent={handleEditEvent}
        />
      )}

      {openModal === "confirmation" && eventSelected && (
        <ConfirmationModalBase
          isOpen={openModal === "confirmation"}
          onClose={onClose}
          acceptText="Eliminar evento"
          title="Eliminar evento"
          description="¿Estás seguro de que deseas eliminar este evento? No podrás deshacer este cambio"
          onAccept={handleDeleteEvent}
          acceptVariant="danger-secondary"
        />
      )}

      <div className="h-full flex flex-col gap-3">
        <p className="font-normal text-body leading-3xl">
          Programa eventos y días hábiles para tu organización.
        </p>

        <div>
          <div className="h-full flex flex-col border-neutral-silent border-neutral border-[0.5px] rounded-xl shadow-neutral-md overflow-hidden">
            <div className="px-3 py-3 w-full flex gap-2 tablet:flex-wrap tablet:gap-0 tablet:items-center justify-between">
              <div className="flex items-center gap-2">
                <Heading variant="h6">
                  Calendario
                  {isLoading && (
                    <Icon
                      data-testid="loading-indicator"
                      type="far"
                      name="spinner-third"
                      className={cn("text-current me-2", "animate-spin")}
                    />
                  )}
                </Heading>
              </div>
              <div className="flex items-center gap-2">
                {canCreate && (
                  <>
                    <Button
                      size="xs"
                      data-testid="config-modal"
                      variant="quiet"
                      type="button"
                      className="relative"
                      onClick={() => setOpenModal("config")}
                    >
                      <span className="hidden md:inline-block">Configurar</span>
                      <Icon type="far" name="gear" className="text-current" />
                    </Button>

                    <Button
                      size="xs"
                      data-testid="create-event-modal"
                      onClick={() => {
                        setSelectedDay({
                          date: new Date(),
                          isAvailable: false,
                          hasEventBlocked: false,
                          events: [],
                        });
                        setOpenModal("event");
                      }}
                    >
                      <span className="hidden md:inline-block">Evento</span>
                      <Icon type="far" name="plus" className="text-current" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="h-full bg-neutral-object-accent-silent flex flex-col">
              <CalendarControls
                currentDate={currentDate}
                handlePrevious={handlePrevious}
                handleNext={handleNext}
                backToDay={backToDay}
              />

              <CalendarTable
                currentDate={currentDate}
                daysEnable={enableDay}
                rows={rows}
                eventsData={eventsData?.length ? eventsData : []}
                openModalDay={(
                  date: Date,
                  isAvailable: boolean,
                  hasEventBlocked,
                  events: EventForDay[],
                ) => {
                  setSelectedDay({
                    date,
                    isAvailable,
                    hasEventBlocked,
                    events,
                  });
                  setOpenDayDrawer(true);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Calendar;
